import * as Sentry from '@sentry/vue'
import { nextTick } from 'vue'
import PresentationApi from '@/api/presentation'
import { scrollToActiveSlide } from '@/utils/dom.utils'
import EventBus from '@/eventBus'
import {
  processCanvasBlocksForSlides,
  determineActiveSlide,
  preserveAnnotationProperties,
  validatePresentationId,
  identifyNewSlides,
} from '@/business-logic/data-third-party-action'
import { trackChatbotSlideCreation } from '@/utils/chatbot-slide-tracking.utils'
import { trackImportSuccessful } from '@/vuex/modules/ai-chatbot/ai-chatbot-import-pptx-pdf.module'

/**
 * Updates presentation state with new slides data while preserving annotation properties
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object} rootState - Vuex root state to get latest slides
 * @param {Array} processedSlides - Slides with canvas blocks processed
 */
const updatePresentationState = async (dispatch, rootState, processedSlides) => {
  await dispatch('setIsNewPresentationAction', false, { root: true })

  // Get the latest slides from store to ensure we have most recent annotation data
  const latestSlides = rootState.presentation?.slides || []

  // Preserve annotation properties from latest slides (not stale originalSlides)
  const slidesWithPreservedAnnotations = preserveAnnotationProperties(processedSlides, latestSlides)

  await dispatch('onlyPatchPresentationAction', { slides: slidesWithPreservedAnnotations }, { root: true })
}

/**
 * Sets the active slide in the store
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object} slideToActivate - The slide to set as active
 */
const setActiveSlideInStore = async (dispatch, slideToActivate) => {
  await dispatch('setSlideActive', slideToActivate, { root: true })
  const slideType = slideToActivate?.slideType || slideToActivate?.type

  // Emit content sync events for supported slide types
  if (slideToActivate && slideType) {
    await dispatch('slideContentSync/handleSlideContentChange', slideToActivate, { root: true })
  }
}

/**
 * Emits slide update event to the event bus
 * @param {Object} slideToActivate - The slide that was activated
 */
const emitSlideUpdateEvent = (slideToActivate) => {
  EventBus.$emit('updateNextSlide', slideToActivate.id)
}

/**
 * Clears annotations for the activated slide
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object} slideToActivate - The slide to clear annotations for
 */
const clearSlideAnnotations = async (dispatch, slideToActivate) => {
  await dispatch('slideAnnotations/clearAnnotationAction', slideToActivate.id, { root: true })
}

/**
 * Sets the active slide and handles related side effects
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object|null} slideToActivate - The slide to set as active
 */
const setActiveSlideIfNeeded = async (dispatch, slideToActivate) => {
  if (!slideToActivate) {
    return
  }

  // Set the active slide in the store
  await setActiveSlideInStore(dispatch, slideToActivate)

  // Emit event for slide update
  emitSlideUpdateEvent(slideToActivate)

  // Clear annotations for the new active slide
  await clearSlideAnnotations(dispatch, slideToActivate)
}

/**
 * Handles DOM updates after slide activation
 */
const performDOMUpdates = async () => {
  await nextTick()
  scrollToActiveSlide()
}

/**
 * Tracks import success before clearing state
 * @param {Object} rootGetters - Vuex root getters to access uploaded file and import option
 */
const trackImportSuccessBeforeClearingState = (rootGetters) => {
  const uploadedFile = rootGetters['aiChatbotImportPptxPdf/aiChatbotUploadedFile']
  const selectedImportOption = rootGetters['aiChatbotImportPptxPdf/selectedImportOption']
  trackImportSuccessful({
    file_name: uploadedFile?.file_metadata?.original_filename,
    file_type: uploadedFile?.file_metadata?.content_type,
    file_metadata: uploadedFile?.file_metadata,
    import_option: selectedImportOption,
    provider: uploadedFile?.import_provider,
    source: uploadedFile?.source,
  })
}

/**
 * Handles the final UI updates after presentation changes
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object|null} slideToActivate - The slide to set as active
 * @param {Object} rootGetters
 * @param {boolean} importInFlight - Whether a v2 import is mid-run on THIS
 *   presentation. Sampled by the caller before its awaits; see below.
 */
const handleUIUpdates = async (dispatch, slideToActivate, rootGetters, importInFlight) => {
  // A presentation refresh that lands MID-import must not touch the viewport or
  // declare the import over (AHA-46792).
  //
  // The clean-up below is an INFERENCE: "an import is running and the slides
  // just changed, therefore it finished". That held while the backend wrote the
  // whole deck in one go — one change meant done. Progressive import writes in
  // batches and then patches captions, so the inference fires on the first
  // batch, ~26s in: the toaster dies, "Your slides are ready!" pops, and the
  // success analytics fire while captioning still has ~55s to run.
  //
  // `importInFlight` is computed by the caller AT ENTRY, before any await — see
  // handleUpdatePresentationToSocketAction. Reading it here instead would read
  // it several awaits late, and two refreshes issued microseconds apart (the
  // last progress tick and the post-terminal one) would both observe the state
  // already cleared and both run the clean-up.

  // Re-activating a slide scrolls the editor to it. Doing that on every batch
  // yanks the viewport away from whatever the user is looking at.
  if (!importInFlight) {
    // Handle slide activation and related side effects
    await setActiveSlideIfNeeded(dispatch, slideToActivate)

    // Perform DOM updates
    await performDOMUpdates()
  }

  // Turn off loading for importing slides
  if (importInFlight) return

  if (rootGetters['aiChatbotImportPptxPdf/isCurrentPresentationImportingSlides']
    || rootGetters['aiChatbotImportPptxPdf/isCurrentPresentationGeneratingSlides']) {
    // Track import success before clearing state
    trackImportSuccessBeforeClearingState(rootGetters)

    dispatch('aiChatbotImportPptxPdf/setImportingSlidesForPresentationIdAction', null, { root: true })
    dispatch('aiChatbotImportPptxPdf/setGeneratingSlidesForPresentationIdAction', null, { root: true })
    dispatch('aiChatbotImportPptxPdf/setAIChatbotUploadedFileAction', null, { root: true })
    dispatch('aiChatbotImportPptxPdf/setSourceFileAction', null, { root: true })
    EventBus.$emit('import: show_import_status_success')
  }
}

/**
 * Fetches updated presentation data from API
 * @param {string} presentationId - The presentation ID to fetch
 * @returns {Promise<Array>} Array of slides from API response
 */
const fetchUpdatedPresentationData = async (presentationId) => {
  const response = await PresentationApi.getPresentationDetails(presentationId)
  return response?.body?.Slides || []
}

/**
 * Gets the first annotated slide ID from stored annotation data
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Function} rootGetters - Vuex root getters function
 * @param {boolean} canUseSlideAnnotations - Whether slide annotations feature is enabled
 * @returns {Promise<string|null>} First slide ID with annotation from socket event, or null
 */
const getFirstAnnotatedSlideId = async (dispatch, rootGetters, canUseSlideAnnotations) => {
  if (!canUseSlideAnnotations) {
    return null
  }

  // Use the stored first annotated slide ID from the annotationsUpdated socket event
  const firstAnnotatedSlideId = rootGetters['slideAnnotations/firstAnnotatedSlideId']

  // Clear the stored value after using it to prevent reuse in subsequent calls
  if (firstAnnotatedSlideId) {
    await dispatch('slideAnnotations/clearFirstAnnotatedSlideId', null, { root: true })
  }

  return firstAnnotatedSlideId
}

const actions = {
  /**
   * Handles socket events for presentation updates after third-party slide generation
   * Updates the presentation data and sets the active slide appropriately
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.dispatch - Vuex dispatch function
   * @param {Object} context.rootState - Root Vuex state
   */
  async handleUpdatePresentationToSocketAction({ dispatch, rootState, rootGetters }, payload = {}) {
    const { presentation } = rootState
    const originalSlides = JSON.parse(JSON.stringify(presentation?.slides || [])) // Deep copy to preserve original state
    const presentationId = payload.presentationId || presentation?.id

    // Early validation
    if (!validatePresentationId(presentationId)) {
      return
    }

    // Abort if a different presentation is loaded (user navigated away mid-import)
    if (presentation?.id && presentation.id !== presentationId) {
      return
    }

    // Read the import guard NOW, before the awaits below (AHA-46792).
    //
    // Two things depend on it being sampled here. The poll loop clears
    // importProgress the instant the job reaches a terminal state, and a
    // refresh dispatched just before that — the last progress tick — is still
    // in flight; reading after the awaits would see it cleared and run the
    // import-finished clean-up a second time, emitting a duplicate success
    // toaster and duplicate success analytics. On a FAILED import that same
    // race can pop "Your slides are ready!" over the error toaster.
    //
    // Scoped by presentationId because importProgress is module state and one
    // import's poll can outlive the user's presence on that deck: without the
    // comparison, navigating away mid-import would suppress slide activation on
    // the NEXT presentation for the remainder of the poll.
    const importProgress = rootGetters['aiChatbotImportPptxPdf/importProgress']
    const importInFlight = Boolean(importProgress)
      && importProgress.presentationId === presentationId

    try {
      // Step 1: Fetch updated presentation data
      const slidesFromApi = await fetchUpdatedPresentationData(presentationId)

      // Step 2: Process slides to fetch canvas blocks
      const processedSlides = await processCanvasBlocksForSlides(slidesFromApi)

      // Step 3: Update presentation state with processed slides
      await updatePresentationState(dispatch, rootState, processedSlides)

      // Step 4: Get first annotated slide ID from stored annotation data
      const canUseSlideAnnotations = rootGetters['slideAnnotations/canUseSlideAnnotations']
      const firstSlideIdWithAnnotation = await getFirstAnnotatedSlideId(
        dispatch,
        rootGetters,
        canUseSlideAnnotations,
      )

      // Step 5: Determine and set the appropriate active slide
      const slideToActivate = determineActiveSlide(
        originalSlides,
        processedSlides,
        firstSlideIdWithAnnotation,
      )

      // Step 6: Handle UI updates
      await handleUIUpdates(dispatch, slideToActivate, rootGetters, importInFlight)

      // Step 7: Track newly created slides for analytics (done at the end to avoid disrupting main flow)
      const newSlides = identifyNewSlides(originalSlides, processedSlides)

      if (newSlides.length > 0) {
        trackChatbotSlideCreation(newSlides, presentation)
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  },
}

export default {
  namespaced: true,
  actions,
}
